import React, { useEffect, useMemo, useState } from "react";
import {
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Button,
  Card,
  Row,
  Col,
  Space,
  Table,
  Modal,
  Upload,
  message,
} from "antd";
import { PlusOutlined, SaveOutlined, UploadOutlined, DeleteOutlined, EditOutlined, SendOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useList, useCustomMutation } from "@refinedev/core";
import { TEAM_LIST_QUERY } from "../../teams/queries";
import { REQUEST_TEMPLATE_LIST_QUERY } from "../../purchase-template/queries";
import {
  ATTACH_FILES_TO_PURCHASE_ORDER_MUTATION,
  REMOVE_FILE_FROM_PURCHASE_ORDER_MUTATION,
} from "../queries";
import { FilePdfOutlined, FileImageOutlined, FileTextOutlined, FileOutlined } from "@ant-design/icons";
import {
  OrderItem,
  Attachment,
  PaymentTerms,
  Currency,
  ShippingMethod,
  ShippingCarrierType,
} from "../interface";
import { VENDOR_LIST_QUERY } from "../../vendors/queries";
import { PurchaseRequest } from "../../purchase-request/interface";

type Props = {
  form: any;
  isEdit?: boolean;
  orderId?: string;
  initialValues?: any;
  initialPurchaseRequest?: PurchaseRequest; // New prop for pre-filling
  onSaveDraft: (values: any) => Promise<void> | void;
  onSubmit: (payload: { orderId?: string; orderData?: any; fiscalYear: number }) => Promise<void> | void;
};

const deriveFileApiBase = () => {
  return import.meta.env.VITE_API_URL
};

const fileUploadUrl = `${deriveFileApiBase()}/file-storage/upload`;
const filePreviewUrl = (fileId: string) => `${deriveFileApiBase()}/file-storage/preview/${fileId}`;

export const PurchaseOrderForm: React.FC<Props> = ({
  form,
  isEdit = false,
  orderId,
  initialValues = {},
  initialPurchaseRequest, // Destructure new prop
  onSaveDraft,
  onSubmit,
}) => {
  const [itemForm] = Form.useForm();
  const [items, setItems] = useState<OrderItem[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isItemModalVisible, setIsItemModalVisible] = useState(false);
  const [vendorSearch, setVendorSearch] = useState<string>("");


  const { mutate: attachFileMutate } = useCustomMutation();
  const { mutate: removeFileMutate } = useCustomMutation();

  const attachments = Form.useWatch("attachments", { form, preserve: true }) || [];
  const shippingMethod = Form.useWatch(["shippingInfo", "method"], form);

  const getMimeTypeIcon = (mimeType: string) => {
    if (mimeType.includes("pdf")) {
      return <FilePdfOutlined />;
    } else if (mimeType.includes("image")) {
      return <FileImageOutlined />;
    } else if (mimeType.includes("text")) {
      return <FileTextOutlined />;
    }
    return <FileOutlined />;
  };

  const formatBytes = (bytes: number, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  // Teams for teamId select
  const { data: teamsData, isLoading: teamsLoading, isError: teamsError } = useList<any>({
    resource: "teams",
    meta: { gqlQuery: TEAM_LIST_QUERY },
  });
  const teams = Array.isArray(teamsData?.data) ? teamsData?.data : (teamsData?.data as any)?.teams?.nodes || [];

  // Purchase Templates
  const { data: templatesData, isLoading: templatesLoading } = useList<any>({
    resource: "purchaseTemplates",
    filters: [{ field: "isActive", operator: "eq", value: "true" }],
    meta: { gqlQuery: REQUEST_TEMPLATE_LIST_QUERY },
  });
  const templates = Array.isArray(templatesData?.data) ? templatesData?.data : (templatesData?.data as any)?.purchaseTemplates?.nodes || [];

  // Vendors for vendor selection using useList of refineJs
  const vendorFilters = [];

  if (vendorSearch) {
    vendorFilters.push({
      field: "companyName",
      operator: "contains" as const,
      value: vendorSearch,
    });
  }

  // Always include the selected vendor if in edit mode
  if (isEdit && initialValues?.vendor?.vendorId) {
    vendorFilters.push({
      field: "id",
      operator: "eq" as const,
      value: initialValues.vendor.vendorId,
    });
  }

  const { data: vendorListData, isLoading: vendorsLoading, refetch: refetchVendors } = useList<any>({
    resource: "vendors",
    filters: vendorFilters,
    meta: { gqlQuery: VENDOR_LIST_QUERY },
  });

  const vendors = Array.isArray(vendorListData?.data) ? vendorListData?.data : (vendorListData?.data as any)?.vendors?.nodes || [];


  // Initialize form defaults
  useEffect(() => {
    if (initialPurchaseRequest) {
      const pr = initialPurchaseRequest;
      const mappedItems: OrderItem[] = pr.items.map((prItem) => ({
        id: prItem.id,
        name: prItem.name,
        description: prItem.description,
        quantity: prItem.quantity,
        unit: prItem.unit,
        unitPrice: prItem.unitPrice,
        totalPrice: prItem.totalPrice,
        specifications: prItem.specifications,
        sourceRequestItemId: prItem.id,
        planId: (prItem.planRef && prItem.planRef.planId) ? prItem.planRef.planId : undefined,
        tags: prItem.tags,
        deliveredQuantity: 0, // Default for new PO
      }));

      form.setFieldsValue({
        teamId: pr.teamId,
        dateCreated: dayjs(), // Current date for PO creation
        orderDeadline: pr.dateRequired ? dayjs(pr.dateRequired) : undefined,
        expectedDeliveryDate: pr.dateRequired ? dayjs(pr.dateRequired) : undefined,
        orderedBy: pr.requestedBy || pr.createdBy,
        vendor: pr.vendor?.vendorId,
        currency: pr.currency,
        paymentTerms: pr.paymentTerms,
        shippingInfo: pr.shippingInfo,
        notes: pr.notes,
        termsAndConditions: pr.termsAndConditions,
        tags: pr.tags,
        attachments: pr.attachments,
      });
      setItems(mappedItems);
      // Set vendor search to pre-fill vendor dropdown
      if (pr.vendor?.companyName) {
        setVendorSearch(pr.vendor.companyName);
      }
    } else if (initialValues) {
      const formValues = {
        dateCreated: initialValues?.dateCreated ? dayjs(initialValues.dateCreated) : undefined,
        orderDeadline: initialValues?.orderDeadline ? dayjs(initialValues.orderDeadline) : undefined,
        expectedDeliveryDate: initialValues?.expectedDeliveryDate ? dayjs(initialValues.expectedDeliveryDate) : undefined,
        ...initialValues,
      };

      form.setFieldsValue(formValues);

      if (initialValues?.items?.length) {
        setItems(initialValues.items);
      }
    }
  }, [initialPurchaseRequest, initialValues, form, vendors]);

  // Handle vendor selection for edit mode
  useEffect(() => {
    if (isEdit && initialValues?.vendor?.vendorId && vendors.length > 0) {
      const vendorExists = vendors.some((v: any) => v.id === initialValues.vendor.vendorId);
      if (vendorExists) {
        form.setFieldsValue({
          vendor: initialValues.vendor.vendorId,
        });
      }
    }
  }, [isEdit, initialValues?.vendor?.vendorId, vendors, form, vendorFilters]);

  // Totals
  const totalAmount = useMemo(
    () => items.reduce((sum, it) => sum + Number(it.totalPrice || 0), 0),
    [items],
  );

  useEffect(() => {
    form.setFieldValue("subtotalAmount", totalAmount);
  }, [totalAmount, form]);

  const subtotal = Form.useWatch("subtotalAmount", form) || 0;
  const tax = Form.useWatch("taxAmount", form) || 0;
  const shipping = Form.useWatch("shippingAmount", form) || 0;
  const discount = Form.useWatch("discountAmount", form) || 0;

  useEffect(() => {
    const nextTotal = subtotal + tax + shipping - discount;
    form.setFieldValue("totalAmount", nextTotal);
  }, [subtotal, tax, shipping, discount, form]);


  // Add or update item
  const saveItem = async () => {
    try {
      const v = await itemForm.validateFields();
      const row: OrderItem = {
        id: v.id || `tmp-${Date.now()}`,
        name: v.name,
        description: v.description,
        quantity: Number(v.quantity),
        unit: v.unit,
        unitPrice: Number(v.unitPrice),
        totalPrice: Number(v.quantity) * Number(v.unitPrice),
        specifications: v.specifications,
        expectedDeliveryDate: v.expectedDeliveryDate ? dayjs(v.expectedDeliveryDate).toDate() : undefined,
        sourceRequestItemId: v.sourceRequestItemId,
        planRef: v.planRef,
        tags: v.tags || [],
        deliveredQuantity: v.deliveredQuantity || 0,
        actualDeliveryDate: v.actualDeliveryDate ? dayjs(v.actualDeliveryDate).toDate() : undefined,
      };
      if (editingIndex !== null && editingIndex >= 0) {
        const copy = [...items];
        copy[editingIndex] = { ...copy[editingIndex], ...row };
        setItems(copy);
      } else {
        setItems((prev) => [...prev, row]);
      }
      setIsItemModalVisible(false);
      itemForm.resetFields();
      setEditingIndex(null);
    } catch (e) {
      // validation errors shown by antd
    }
  };

  const editItem = (index: number) => {
    const it = items[index];
    itemForm.setFieldsValue({
      ...it,
      expectedDeliveryDate: it.expectedDeliveryDate ? dayjs(it.expectedDeliveryDate) : undefined,
      actualDeliveryDate: it.actualDeliveryDate ? dayjs(it.actualDeliveryDate) : undefined,
    });
    setEditingIndex(index);
    setIsItemModalVisible(true);
  };

  const removeItem = (index: number) => {
    const copy = [...items];
    copy.splice(index, 1);
    setItems(copy);
  };

  // Build payload for save/update
  const buildPayload = () => {
    const fv = form.getFieldsValue();
    const selectedVendor = vendors.find((v: any) => v.id === fv.vendor);

    const payload = {
      templateId: fv.templateId,
      teamId: fv.teamId,
      dateCreated: fv.dateCreated ? fv.dateCreated.toDate() : new Date(),
      orderDeadline: fv.orderDeadline ? fv.orderDeadline.toDate() : new Date(),
      expectedDeliveryDate: fv.expectedDeliveryDate ? fv.expectedDeliveryDate.toDate() : undefined,
      orderedBy: fv.orderedBy,
      vendor: selectedVendor ? {
        vendorId: selectedVendor.id,
        companyName: selectedVendor.companyName,
        contactPerson: selectedVendor.contact?.name,
        email: selectedVendor.contact?.address?.email,
        phone: selectedVendor.contact?.address?.phones?.[0],
        address: selectedVendor.address?.addressLine,
        tin: selectedVendor.tin,
      } : undefined,
      purchaseRequestRef: fv.purchaseRequestRef, // Assuming this will be handled by a separate component/modal
      items: items.map((it) => ({
        id: it.id?.startsWith("tmp-") ? undefined : it.id, // Remove tmp id for new items
        name: it.name,
        description: it.description,
        quantity: it.quantity,
        unit: it.unit,
        unitPrice: it.unitPrice,
        totalPrice: it.totalPrice,
        specifications: it.specifications,
        expectedDeliveryDate: it.expectedDeliveryDate,
        sourceRequestItemId: it.sourceRequestItemId,
        planId: it.planRef?.planId,
        tags: it.tags,
        deliveredQuantity: it.deliveredQuantity,
        actualDeliveryDate: it.actualDeliveryDate,
      })),
      subtotalAmount: totalAmount,
      taxAmount: fv.taxAmount,
      shippingAmount: fv.shippingAmount,
      discountAmount: fv.discountAmount,
      totalAmount: totalAmount + (fv.taxAmount || 0) + (fv.shippingAmount || 0) - (fv.discountAmount || 0),
      currency: fv.currency,
      exchangeRate: fv.exchangeRate,
      paymentTerms: fv.paymentTerms,
      shippingInfo: fv.shippingInfo,
      attachments: fv.attachments || [],
      notes: fv.notes,
      termsAndConditions: fv.termsAndConditions,
      tags: fv.tags,
      approvals: fv.approvals,
      fiscalYear: fv.fiscalYear, // Add fiscalYear to payload
    };
    return payload;
  };

  // Handlers
  const handleSaveDraft = async () => {
    await form.validateFields();
    await onSaveDraft(buildPayload());
  };

  const handleSubmit = async () => {
    await form.validateFields();
    const fiscalYear = form.getFieldValue("fiscalYear");
    if (!fiscalYear) {
      message.error("Fiscal Year is required for submission");
      return;
    }
    const payload = buildPayload();
    console.log("Submitting Purchase Order with payload:", payload);
    if (orderId) {
      await onSubmit({ orderId, orderData: payload, fiscalYear: Number(fiscalYear) });
    } else {
      await onSubmit({ orderData: payload, fiscalYear: Number(fiscalYear) });
    }
  };

  // File upload
  const uploadProps = {
    name: "file",
    multiple: true,
    maxCount: 5, // Limit to 5 files
    beforeUpload: (file: File) => {
      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "application/pdf",
        "application/msword", // .doc
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
        "application/vnd.ms-excel", // .xls
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
      ];
      const isAllowedType = allowedTypes.includes(file.type);
      if (!isAllowedType) {
        message.error(`You can only upload JPG/PNG/PDF/DOC/DOCX/XLS/XLSX file!`);
      }
      const isLt10M = file.size / 1024 / 1024 < 10; // 10MB limit
      if (!isLt10M) {
        message.error("File must be smaller than 10MB!");
      }
      return isAllowedType && isLt10M;
    },
    customRequest: async (options: any) => {
      const { file, onSuccess, onError } = options;
      try {
        const fd = new FormData();
        fd.append("file", file as Blob);
        const resp = await fetch(fileUploadUrl, {
          method: "POST",
          body: fd,
          credentials: "include",
        });
        if (!resp.ok) {
          const txt = await resp.text();
          throw new Error(txt || `Upload failed: ${resp.status}`);
        }
        const json = await resp.json();
        const fileData = json?.data;
        if (!fileData || !fileData.fileId) {
          throw new Error("Upload response missing file data or fileId");
        }
        const currentAttachments = form.getFieldValue("attachments") || [];
        const attachmentData: Attachment = {
          fileId: fileData.fileId,
          originalName: fileData.originalName,
          mimeType: fileData.mimeType,
          size: fileData.size,
          category: "GENERAL", // Default category
        };
        const next = [...currentAttachments, attachmentData];
        form.setFieldValue("attachments", next);
        onSuccess?.(json, file);

        // Call backend mutation to attach file if orderId exists (i.e., editing an existing PO)
        if (orderId) {
          attachFileMutate({
            url: "",
            method: "post",
            values: {
              input: {
                orderId: orderId,
                attachments: [attachmentData],
              },
            },
            meta: { gqlMutation: ATTACH_FILES_TO_PURCHASE_ORDER_MUTATION },
          });
        }

      } catch (e: any) {
        console.error(e);
        message.error(e?.message || "Upload failed");
        onError?.(e);
      }
    },
    showUploadList: false,
  };

  const handleRemoveAttachment = (fileIdToRemove: string) => {
    const currentAttachments = attachments || [];
    const next = currentAttachments.filter((a: Attachment) => a.fileId !== fileIdToRemove);
    form.setFieldValue("attachments", next);
    message.success("Attachment removed locally");

    // Call backend mutation to remove file if orderId exists
    if (orderId) {
      removeFileMutate({
        url: "",
        method: "post",
        values: {
          input: {
            orderId: orderId,
            fileId: fileIdToRemove,
          },
        },
        meta: { gqlMutation: REMOVE_FILE_FROM_PURCHASE_ORDER_MUTATION },
      });
    }
  };

  // Columns for Order Items
  const columns: any[] = [
    { title: "Name", dataIndex: "name" },
    { title: "Description", dataIndex: "description" },
    { title: "Qty", dataIndex: "quantity" },
    { title: "Unit", dataIndex: "unit" },
    {
      title: "Unit Price",
      dataIndex: "unitPrice",
      render: (v: number) => `${form.getFieldValue("currency") || Currency.ETB} ${Number(v || 0).toLocaleString()}`,
    },
    {
      title: "Total",
      dataIndex: "totalPrice",
      render: (v: number) => `${form.getFieldValue("currency") || Currency.ETB} ${Number(v || 0).toLocaleString()}`,
    },
    { title: "Expected Delivery", dataIndex: "expectedDeliveryDate", render: (v: string) => v ? dayjs(v).format("YYYY-MM-DD") : "-" },
    {
      title: "Actions",
      render: (_: any, _r: OrderItem, i: number) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => editItem(i)} />
          <Button type="link" danger icon={<DeleteOutlined />} onClick={() => removeItem(i)} />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          currency: Currency.ETB,
          paymentTerms: PaymentTerms.NET_30,
          shippingInfo: { method: ShippingMethod.SUPPLIER_DELIVERY },
          fiscalYear: new Date().getFullYear(), // Add fiscalYear to initialValues
          ...initialValues,
        }}
      >
        <Card title="Order Information" style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            {isEdit && (
              <Col span={8}>
                <Form.Item name="orderNo" label="PO Number">
                  <Input disabled />
                </Form.Item>
              </Col>
            )}
            <Col span={isEdit ? 8 : 12}>
              <Form.Item
                name="templateId"
                label="Purchase Template"
              >
                <Select
                  placeholder="Select a template to pre-fill fields"
                  loading={templatesLoading}
                  allowClear
                  onChange={(value) => {
                    if (value) {
                      const selectedTemplate = templates.find((t: any) => t.id === value);
                      if (selectedTemplate) {
                        form.setFieldValue("teamId", selectedTemplate.teamId);
                        form.setFieldValue("fiscalYear", selectedTemplate.fiscalYear); // Set fiscal year from template
                        // Copy approval workflow from template
                        form.setFieldValue("approvals", selectedTemplate.steps);
                        form.setFieldValue("approvalType", selectedTemplate.approvalType);
                        form.setFieldValue("externalWorkflow", selectedTemplate.externalWorkflow);
                        message.success(`Template '${selectedTemplate.name}' applied.`);
                      }
                    } else {
                      form.setFieldValue("teamId", undefined);
                      form.setFieldValue("fiscalYear", undefined); // Clear fiscal year
                      form.setFieldValue("approvals", undefined);
                      form.setFieldValue("approvalType", undefined);
                      form.setFieldValue("externalWorkflow", undefined);
                    }
                  }}
                >
                  {(templates as any[]).map((t: any) => (
                    <Select.Option key={t.id} value={t.id}>
                      {t.name} ({t.fiscalYear})
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={isEdit ? 8 : 12}>
              <Form.Item
                name="teamId"
                label="Team"
                rules={[{ required: true, message: "Please select a team" }]}
              >
                <Select placeholder="Select team" loading={teamsLoading} disabled={teamsLoading || teamsError}>
                  {(teams as any[]).map((t: any) => (
                    <Select.Option key={t.id} value={t.id}>
                      {t.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="fiscalYear"
                label="Fiscal Year (for Submission)"
                rules={[{ required: true, message: "Fiscal Year is required for submission" }]}
              >
                <InputNumber min={2020} max={2100} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="dateCreated"
                label="Date Created"
                rules={[{ required: true, message: "Please pick date created" }]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="orderDeadline"
                label="Order Deadline"
                rules={[{ required: true, message: "Please pick order deadline" }]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="expectedDeliveryDate"
                label="Expected Delivery Date"
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="vendor"
                label="Vendor"
                rules={[{ required: true, message: "Please select a vendor" }]}
              >
                <Select
                  showSearch
                  placeholder="Select or search vendor"
                  loading={vendorsLoading}
                  onSearch={(value) => setVendorSearch(value)}
                  filterOption={false}
                  notFoundContent={vendorsLoading ? "Loading..." : "No vendors found"}
                >
                  {vendors.map((v: any) => (
                    <Select.Option key={v.id} value={v.id}>
                      {v.companyName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="orderedBy" label="Ordered By">
                <Input placeholder="Ordered By (User ID)" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="currency"
                label="Currency"
                rules={[{ required: true, message: "Please select currency" }]}
              >
                <Select placeholder="Select currency">
                  {Object.values(Currency).map((c) => (
                    <Select.Option key={c} value={c}>
                      {c}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="paymentTerms"
                label="Payment Terms"
                rules={[{ required: true, message: "Please select payment terms" }]}
              >
                <Select placeholder="Select payment terms">
                  {Object.values(PaymentTerms).map((pt) => (
                    <Select.Option key={pt} value={pt}>
                      {pt}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name={["shippingInfo", "method"]}
                label="Shipping Method"
                rules={[{ required: true, message: "Please select shipping method" }]}
              >
                <Select placeholder="Select shipping method">
                  {Object.values(ShippingMethod).map((sm) => (
                    <Select.Option key={sm} value={sm}>
                      {sm}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            {shippingMethod === ShippingMethod.THIRD_PARTY_SHIPPER && (
              <>
                <Col span={8}>
                  <Form.Item name={["shippingInfo", "carrierType"]} label="Carrier Type">
                    <Select placeholder="Select carrier type" allowClear>
                      {Object.values(ShippingCarrierType).map((sct) => (
                        <Select.Option key={sct} value={sct}>
                          {sct}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name={["shippingInfo", "carrierName"]} label="Carrier Name">
                    <Input placeholder="Carrier Name" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name={["shippingInfo", "trackingNumber"]} label="Tracking Number">
                    <Input placeholder="Tracking Number" />
                  </Form.Item>
                </Col>
              </>
            )}
            <Col span={24}>
              <Form.Item name={["shippingInfo", "shippingAddress"]} label="Shipping Address">
                <Input.TextArea rows={2} placeholder="Shipping Address" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name={["shippingInfo", "specialInstructions"]} label="Special Instructions">
                <Input.TextArea rows={2} placeholder="Special Instructions" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="notes" label="Notes">
                <Input.TextArea rows={2} placeholder="Notes" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="termsAndConditions" label="Terms and Conditions">
                <Input.TextArea rows={3} placeholder="Terms and Conditions" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          title="Items"
          style={{ marginBottom: 24 }}
          extra={
            <Button
              type="primary"
              shape="circle"
              icon={<PlusOutlined />}
              size="large"
              onClick={() => {
                itemForm.resetFields();
                setEditingIndex(null);
                setIsItemModalVisible(true);
              }}
            />
          }
        >
          <Table
            dataSource={items}
            columns={columns}
            rowKey={(r) => r.id || `${r.name}-${r.quantity}-${r.unitPrice}`}
            pagination={false}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={5}>
                  <strong>Total</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <strong>{form.getFieldValue("currency") || Currency.ETB} {totalAmount.toLocaleString()}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} colSpan={2}></Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
        </Card>

        <Card
          title="Financials"
          style={{ marginBottom: 24 }}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="subtotalAmount" label="Subtotal" initialValue={0}>
                <InputNumber disabled style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="taxAmountX" label="Tax Amount" initialValue={0}>
                <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="shippingAmount" label="Shipping Amount" initialValue={0}>
                <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="discountAmount" label="Discount Amount" initialValue={0}>
                <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="totalAmount" label="Total Amount" initialValue={0}>
                <InputNumber disabled style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          title="Attachments"
          style={{ marginBottom: 24 }}
          extra={
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />}>
                Upload
              </Button>
            </Upload>
          }
        >
          <Space wrap>
            {attachments.map((att: Attachment) => (
              <div key={att.fileId} style={{ border: "1px solid #eee", padding: 8, borderRadius: 6 }}>
                <Space>
                  {getMimeTypeIcon(att.mimeType)}
                  <a href={filePreviewUrl(att.fileId)} target="_blank" rel="noreferrer">
                    {att.originalName} ({formatBytes(att.size)})
                  </a>
                  <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleRemoveAttachment(att.fileId)}>
                    Remove
                  </Button>
                </Space>
              </div>
            ))}
          </Space>
          {!attachments?.length && <div>No attachments</div>}
          
          <Form.Item name="attachments" style={{ display: "none" }}>
            <Input />
          </Form.Item>
        </Card>

        <Card style={{ textAlign: "right" }}>
          <Space>
            <Button type="default" onClick={handleSaveDraft} icon={<SaveOutlined />}>
              Save Draft
            </Button>
            <Button type="primary" onClick={handleSubmit} icon={<SendOutlined />}>
              Save and Submit
            </Button>
          </Space>
        </Card>
      </Form>

      <Modal
        title={editingIndex !== null ? "Edit Item" : "Add Item"}
        open={isItemModalVisible}
        onOk={saveItem}
        onCancel={() => {
          setIsItemModalVisible(false);
          itemForm.resetFields();
          setEditingIndex(null);
        }}
        okText={editingIndex !== null ? "Update Item" : "Add Item"}
        width={820}
        destroyOnClose
      >
        <Form form={itemForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="name" label="Name" rules={[{ required: true, message: "Please enter name" }]}>
                <Input placeholder="Item name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="unit" label="Unit">
                <Input placeholder="e.g., Pcs, Kg, Box" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="description" label="Description">
                <Input.TextArea rows={2} placeholder="Description" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="quantity" label="Quantity" rules={[{ required: true, message: "Enter quantity" }]}>
                <InputNumber min={0.0000001} step={0.1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="unitPrice" label="Unit Price" rules={[{ required: true, message: "Enter unit price" }]}>
                <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="expectedDeliveryDate" label="Expected Delivery Date">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="specifications" label="Specifications">
                <Input.TextArea rows={2} placeholder="Detailed specifications" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="tags" label="Tags">
                <Select mode="tags" placeholder="Add tags" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};